import { Avatar, Box, Typography, Stack, IconButton, Tooltip } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { useTranslation } from "react-i18next";

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SystemMessage = ({ message }) => {
  const { t } = useTranslation("chat");
  const eventKey = message.systemEvent || "";
  const displayText = t(`system.${eventKey}`, message.body);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        my: 1.5,
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
          backgroundColor: "rgba(7,31,40,0.04)",
          borderRadius: "20px",
          px: 2,
          py: 0.75,
          maxWidth: "80%",
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: 12,
            fontWeight: 500,
            textAlign: "center",
          }}
        >
          {displayText}
        </Typography>
      </Stack>
    </Box>
  );
};

export const MessageBubble = ({ message, isOwn, onReport, showSeenAvatar, seenByUser }) => {
  const { t } = useTranslation("chat");

  if (message.messageType === "system") {
    return <SystemMessage message={message} />;
  }

  const isFailed = message._failed;
  const isPending = message._pending;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isOwn ? "flex-end" : "flex-start",
        mb: showSeenAvatar ? 0.25 : 0.75,
        px: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: "82%", md: "65%" },
          minWidth: 60,
          position: "relative",
          "&:hover .report-btn": { opacity: 1 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            backgroundColor: isOwn
              ? "primary.main"
              : "rgba(7,31,40,0.05)",
            color: isOwn ? "white" : "text.primary",
            borderRadius: isOwn
              ? "16px 16px 4px 16px"
              : "16px 16px 16px 4px",
            px: 2,
            py: 1.25,
            opacity: isFailed ? 0.7 : isPending ? 0.85 : 1,
            boxShadow: isOwn
              ? "0 1px 4px rgba(254,31,2,0.15)"
              : "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {message.body}
          </Typography>

          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            spacing={0.5}
            sx={{ mt: 0.5, minHeight: 16 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                color: isOwn ? "rgba(255,255,255,0.7)" : "text.disabled",
              }}
            >
              {formatTime(message.createdAt)}
            </Typography>

            {isOwn && !isFailed && !isPending && (
              message.isRead ? (
                <DoneAllIcon
                  sx={{
                    fontSize: 14,
                    color: isOwn ? "rgba(255,255,255,0.85)" : "primary.main",
                  }}
                />
              ) : (
                <DoneIcon
                  sx={{
                    fontSize: 14,
                    color: isOwn ? "rgba(255,255,255,0.55)" : "text.disabled",
                  }}
                />
              )
            )}

            {isFailed && (
              <ErrorOutlineIcon
                sx={{ fontSize: 14, color: isOwn ? "#ffcdd2" : "#d32f2f" }}
              />
            )}
          </Stack>
        </Box>

        {/* Seen avatar — Facebook Messenger style */}
        {showSeenAvatar && seenByUser && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 0.25,
              mb: 0.5,
            }}
          >
            <Tooltip title={`Seen by ${seenByUser.displayName || ""}`} placement="left">
              <Avatar
                src={seenByUser.profilePicture || undefined}
                alt={seenByUser.displayName || ""}
                sx={{
                  width: 16,
                  height: 16,
                  fontSize: 8,
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                {seenByUser.displayName?.[0]?.toUpperCase() || "?"}
              </Avatar>
            </Tooltip>
          </Box>
        )}

        {/* Failed retry indicator */}
        {isFailed && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: isOwn ? "right" : "left",
              color: "#d32f2f",
              fontSize: 11,
              mt: 0.25,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {t("errors.sendFailed")}
          </Typography>
        )}

        {/* Report (only for received messages) */}
        {!isOwn && !message._pending && message.messageType !== "system" && (
          <Tooltip title={t("report")} placement="right">
            <IconButton
              size="small"
              className="report-btn"
              onClick={() => onReport?.(message._id)}
              sx={{
                opacity: 0,
                transition: "opacity 150ms",
                position: "absolute",
                right: isOwn ? undefined : -32,
                left: isOwn ? -32 : undefined,
                top: 4,
                color: "text.disabled",
              }}
            >
              <FlagOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
